// Parseur métier partagé entre l'application et la fonction e-mail.
const clean = (value) =>
  String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeText = (value) =>
  clean(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();

const parseFrenchDate = (value) => {
  const match = clean(value).match(
    /\b(\d{1,2})[/. -](\d{1,2})[/. -](\d{2,4})\b/
  );

  if (!match) return null;

  const year =
    match[3].length === 2 ? Number(`20${match[3]}`) : Number(match[3]);

  return `${year}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
};

const joinItems = (items) =>
  clean(
    [...items]
      .sort((first, second) => {
        if (Math.abs(first.y - second.y) > 2.5) return first.y - second.y;
        return first.x - second.x;
      })
      .map((item) => item.text)
      .join(' ')
  );

function groupLines(items, tolerance = 2.5) {
  const lines = [];

  for (const item of [...items].sort((a, b) => a.y - b.y || a.x - b.x)) {
    let line = lines.find((candidate) => Math.abs(candidate.y - item.y) <= tolerance);

    if (!line) {
      line = { y: item.y, items: [] };
      lines.push(line);
    }

    line.items.push(item);
    line.y =
      line.items.reduce((total, current) => total + current.y, 0) /
      line.items.length;
  }

  return lines
    .map((line) => ({
      ...line,
      items: line.items.sort((a, b) => a.x - b.x),
      text: joinItems(line.items),
    }))
    .sort((a, b) => a.y - b.y);
}

export function normalizePodLdv(value) {
  const match = clean(value)
    .toUpperCase()
    .match(/\b(0V[A-Z0-9]{10})(?:\s+\d{2})?\b/);

  return match ? match[1] : '';
}

function extractRecipient(lines, width, height) {
  const rightLines = lines
    .map((line) => {
      const items = line.items.filter(
        (item) => item.x >= width * 0.48 && item.x < width * 0.8
      );

      return {
        ...line,
        items,
        text: joinItems(items),
      };
    })
    .filter((line) => line.text);

  const labelIndex = rightLines.findIndex((line) =>
    line.y > height * 0.18 &&
    normalizeText(line.text).includes('destinataire')
  );

  if (labelIndex < 0) return { recipient: '', city: '', stefCode: '' };

  const label = rightLines[labelIndex];
  const stefCode =
    label.text.match(/\(([A-Z0-9]{5,10})\)/i)?.[1]?.toUpperCase() || '';
  const following = rightLines.slice(labelIndex + 1).filter(
    (line) => line.y <= label.y + 35
  );

  const recipientLine = following.find((line) => {
    const value = normalizeText(line.text);
    return (
      value &&
      !value.includes('telephone') &&
      !/\b(?:FR\s*-\s*)?\d{5}\b/i.test(line.text)
    );
  });

  const cityLine = following.find((line) =>
    /\b(?:FR\s*-\s*)?\d{5}\s+(.+)/i.test(line.text)
  );
  const cityMatch = cityLine?.text.match(/\b(?:FR\s*-\s*)?\d{5}\s+(.+)/i);

  return {
    recipient: clean(recipientLine?.text),
    city: clean(cityMatch?.[1]),
    stefCode,
  };
}

function extractSigner(lines, width, height) {
  const labelIndex = lines.findIndex(
    (line) =>
      line.y < height / 2 &&
      normalizeText(line.text).includes('nomdusignataire')
  );

  if (labelIndex < 0) return '';

  const label = lines[labelIndex];
  const inlineSigner = joinItems(
    label.items.filter((item) => item.x >= width * 0.55)
  );

  if (/^[A-ZÀ-ÖØ-Þ][A-ZÀ-ÖØ-Þ' -]{1,50}$/u.test(inlineSigner)) {
    return inlineSigner;
  }

  const candidate = [...lines]
    .slice(0, labelIndex)
    .reverse()
    .find(
      (line) =>
        label.y - line.y <= 15 &&
        line.items.some((item) => item.x >= width * 0.48) &&
        /^[A-ZÀ-ÖØ-Þ][A-ZÀ-ÖØ-Þ' -]{1,50}$/u.test(clean(line.text))
    );

  return clean(candidate?.text);
}

function hasDeliveryReservations(lines, width, height) {
  const label = lines.find(
    (line) =>
      line.y < height / 2 &&
      line.items.some((item) => item.x < width * 0.48) &&
      normalizeText(line.text).includes('reservasalalivraison')
  );

  if (!label) return false;

  return lines.some(
    (line) =>
      line.y > label.y + 3 &&
      line.y < height / 2 &&
      line.items.some((item) => item.x < width * 0.48) &&
      clean(line.text).length > 2
  );
}

function extractLastDeliveryDateTime(lines, height) {
  const matches = [];

  for (const line of lines) {
    if (line.y >= height / 2) continue;

    const match = line.text.match(
      /\b(\d{1,2}[/.]\d{1,2}[/.]\d{2,4})\s+(\d{1,2}:\d{2})\b/
    );

    if (match) {
      matches.push({
        y: line.y,
        date: parseFrenchDate(match[1]),
        time: match[2],
      });
    }
  }

  return matches.sort((first, second) => second.y - first.y)[0] || null;
}

export function parsePodPages(pages) {
  if (!pages?.length) {
    throw new Error('Le POD ne contient aucune page lisible.');
  }

  const firstPage = pages[0];
  const firstCopyItems = firstPage.items.filter(
    (item) => item.y < firstPage.height / 2
  );
  const lines = groupLines(firstCopyItems);
  const text = joinItems(firstCopyItems);
  const ldvRawMatch = text.match(/\b0V[A-Z0-9]{10}(?:\s+\d{2})?\b/i);
  const ldvRaw = clean(ldvRawMatch?.[0]).toUpperCase();
  const ldv = normalizePodLdv(ldvRaw);
  const blNumber =
    text.match(
      /\bBL\s+du\s+\d{1,2}[/.]\d{1,2}[/.]\d{2,4}\s+\(([A-Z0-9/._-]+)\)/i
    )?.[1] ||
    text.match(/\bN\s*[°º]?\s*BL\s+([A-Z0-9/._-]+)/i)?.[1] ||
    '';
  const parcelMatch = text.match(/\b(\d+)\s*CS\b/i);
  const parcelCount = parcelMatch ? Number(parcelMatch[1]) : null;
  const materialMatch = text.match(
    /\b\d+\s*CS\s+(\d+(?:[.,]\d+)?)\b/i
  );
  const materialLine = lines.find((line) =>
    /\b\d+\s*(?:CS|PA)\b/i.test(line.text)
  );
  const weightItem = materialLine?.items.find(
    (item) =>
      item.x >= firstPage.width * 0.4 &&
      item.x < firstPage.width * 0.5 &&
      /^\d+(?:[.,]\d+)?$/.test(item.text)
  );
  const weightRaw = materialMatch?.[1] || weightItem?.text;
  const weightKg = weightRaw
    ? Number(weightRaw.replace(',', '.'))
    : null;
  const palletMatch = text.match(/\b(\d+)\s*PA\b/i);
  const palletCount = palletMatch ? Number(palletMatch[1]) : null;
  const remiseDate = parseFrenchDate(
    text.match(
      /\bRemise\s+du\s+(\d{1,2}[/.]\d{1,2}[/.]\d{2,4})/i
    )?.[1]
  );
  const delivery = extractLastDeliveryDateTime(lines, firstPage.height);
  const signerName = extractSigner(lines, firstPage.width, firstPage.height);
  const recipient = extractRecipient(
    lines,
    firstPage.width,
    firstPage.height
  );
  const hasReservations = hasDeliveryReservations(
    lines,
    firstPage.width,
    firstPage.height
  );
  const signedGoodCondition = Boolean(
    signerName &&
      delivery?.date &&
      normalizeText(text).includes('marchandisesrecuesenbonetat')
  );

  const result = {
    ldv_raw: ldvRaw,
    ldv_number: ldv,
    bl_number: clean(blNumber),
    stef_code: recipient.stefCode,
    recipient: recipient.recipient,
    city: recipient.city,
    parcel_count: parcelCount,
    weight_kg: weightKg,
    pallet_count: palletCount,
    remise_date: remiseDate,
    actual_delivery_date: delivery?.date || null,
    actual_delivery_time: delivery?.time || null,
    signer_name: signerName || null,
    signed_good_condition: signedGoodCondition,
    has_reservations: hasReservations,
  };

  const required = [
    'ldv_number',
    'bl_number',
    'recipient',
    'city',
    'weight_kg',
    'actual_delivery_date',
  ];
  const missing = required.filter(
    (field) =>
      result[field] === null ||
      result[field] === undefined ||
      result[field] === ''
  );

  if (missing.length) {
    throw new Error(
      `POD incomplet : ${missing.join(', ')} non reconnu(s).`
    );
  }

  return result;
}

const numbersEqual = (first, second, tolerance = 0) => {
  if (first === null || first === undefined || first === '') return false;
  if (second === null || second === undefined || second === '') return false;
  return Math.abs(Number(first) - Number(second)) <= tolerance;
};

export function scorePodCandidate(shipment, pod) {
  const matches = [];
  const conflicts = [];
  let score = 0;

  const compareText = (label, first, second, points) => {
    if (!first || !second) return;
    if (normalizeText(first) === normalizeText(second)) {
      score += points;
      matches.push(label);
    } else {
      conflicts.push(label);
    }
  };

  compareText('Même numéro de BL', shipment.bl_number, pod.bl_number, 8);
  compareText('Même destinataire', shipment.recipient, pod.recipient, 4);
  compareText('Même ville', shipment.city, pod.city, 3);

  if (numbersEqual(shipment.parcel_count, pod.parcel_count)) {
    score += 3;
    matches.push('Même nombre de colis');
  } else if (shipment.parcel_count && pod.parcel_count) {
    conflicts.push('Nombre de colis différent');
  }

  if (numbersEqual(shipment.weight_kg, pod.weight_kg, 1)) {
    score += 3;
    matches.push('Même poids');
  } else if (shipment.weight_kg && pod.weight_kg) {
    conflicts.push('Poids différent');
  }

  if (
    pod.pallet_count &&
    shipment.pallet_count &&
    numbersEqual(shipment.pallet_count, pod.pallet_count)
  ) {
    score += 2;
    matches.push('Même nombre de palettes');
  } else if (pod.pallet_count && shipment.pallet_count) {
    conflicts.push('Nombre de palettes différent');
  }

  const blMatches = matches.includes('Même numéro de BL');
  const recipientMatches = matches.includes('Même destinataire');
  const cityMatches = matches.includes('Même ville');
  const logisticsMatches = matches.filter((label) =>
    [
      'Même nombre de colis',
      'Même poids',
      'Même nombre de palettes',
    ].includes(label)
  ).length;

  return {
    score,
    reasons: matches,
    conflicts,
    automatic:
      blMatches &&
      recipientMatches &&
      cityMatches &&
      logisticsMatches >= 2 &&
      conflicts.length === 0,
  };
}

export const __podTestables = {
  clean,
  groupLines,
  normalizeText,
  parseFrenchDate,
};


